import React, { ReactNode } from 'react'
import AppNavbar from './navbar'
import { Company, Widget } from '@/types'
import useTheme, { ThemeContextProvider } from '@/contexts/useTheme'
import Script from 'next/script'
import { CartContextProvider } from '@/contexts/useCart'
import IframeHeightReporter from '../iframe-height-reporter'
import CookieBanner from '../cookie-banner'
import TranslationWarningBanner from '../translation-warning-banner'
import { useIsInIframe } from '@/helper/utils'

const AppProviders = ({
  children,
  widget,
  company,
}: {
  company?: Company
  children: ReactNode
  widget: Widget
}) => {
  return (
    <ThemeContextProvider company={company ?? widget?.company}>
      <AppLayout widget={widget}>{children}</AppLayout>
    </ThemeContextProvider>
  )
}

const AppLayout = ({
  children,
  widget,
}: {
  children: ReactNode
  widget: Widget
}) => {
  const { theme } = useTheme()
  const isInIframe = useIsInIframe()

  // Extract domain from cross-domain URL for Google Analytics linker configuration
  const getCrossDomainConfig = () => {
    if (!theme?.googleAnalyticsCrossDomainUrl) {
      return ''
    }

    try {
      // If it's a full URL, extract the domain; otherwise use as-is
      const url = theme.googleAnalyticsCrossDomainUrl.trim()
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url)
        return urlObj.hostname
      }
      // If it's just a domain, use it directly
      return url
    } catch {
      // If URL parsing fails, return empty string
      return ''
    }
  }

  const crossDomain = getCrossDomainConfig()
  const gtagConfig = crossDomain
    ? `{
          page_path: window.location.pathname,
          linker: {
            accept_incoming: true,
            domains: ['${crossDomain}']
          }
        }`
    : `{
          page_path: window.location.pathname,
        }`

  return (
    <>
      {theme?.googleAnalyticsId ? (
        <>
          {/* Global Site Tag (gtag.js) - Google Analytics */}
          <Script
            strategy="lazyOnload"
            src={`https://www.googletagmanager.com/gtag/js?id=${theme.googleAnalyticsId}`}
          />
          <Script
            strategy="lazyOnload"
            id="googel_analytics"
            dangerouslySetInnerHTML={{
              __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${theme.googleAnalyticsId}', ${gtagConfig});
          `,
            }}
          />
        </>
      ) : null}
      <CartContextProvider>
        <TranslationWarningBanner />
        <section
          translate="no"
          className={`pt-24 md:pt-24 flex flex-col bg-white notranslate ${
            isInIframe ? '' : 'min-h-screen justify-between'
          }`}
        >
          {widget && <AppNavbar widget={widget} />}

          <div id="app-main-content" className="px-4 max-w-7xl mx-auto w-full md:mb-12">
            {children}
          </div>

          {/* {widget && <AppFooter company={widget.company} />} */}
          <IframeHeightReporter />
          <CookieBanner />
        </section>
      </CartContextProvider>
    </>
  )
}

export default AppProviders
